import { Component, Input, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import * as dayjs from 'dayjs';
import { GlobalService } from 'src/app/api/global.service';

@Component({
  selector: 'app-create',
  templateUrl: './create.page.html',
  styleUrls: ['./create.page.scss'],
})
export class CreatePage implements OnInit {

  // When set (by the top-navbar "Manage Appointment" button), this page manages/reschedules/cancels
  // this existing self-booked appointment instead of creating a new one - team members become fixed.
  @Input() existing_booking: any = null;

  user: any = null;
  companies: any[] = [];
  // Either the literal string 'myself' or a selected company object from `companies`
  selected_target: any = 'myself';

  loading_team_members = true;
  loading_slots = false;
  loading_dates = false;
  allowed_team_members: any[] = [];
  available_slots: any[] = [];

  available_dates_set: Set<string> = new Set();
  calendar_disabled_dates: Date[] = [];
  calendar_date: Date | null = null;
  min_date = new Date();

  displayed_year = new Date().getFullYear();
  displayed_month = new Date().getMonth() + 1; // 1-12

  // The existing booking's own start/end (manage mode only) - the availability endpoint won't ever
  // return this slot itself (it doesn't exclude the appointment being managed when checking conflicts),
  // so it's re-added into available_slots manually whenever it's still what's being viewed/kept.
  private original_slot: { start_time: string; end_time: string } | null = null;

  appointment_info = {
    title: '',
    description: '',
    date: '',
    in_hours: 1,
    in_minutes: 0,
    selected_length: 60,
    selected_team_members: [] as string[],
    selected_slot: null as { start_time: string; end_time: string } | null,
  };

  constructor(
    private modalController: ModalController,
    private alertController: AlertController,
    private apiService: ApiService,
    public globalService: GlobalService
  ) {}

  get is_managing(): boolean {
    return !!this.existing_booking;
  }

  ngOnInit() {
    if (this.is_managing) {
      this.appointment_info.title = this.existing_booking.title || '';
      this.appointment_info.description = this.existing_booking.description || '';
      this.allowed_team_members = this.existing_booking.team_members || [];
      this.appointment_info.selected_team_members = this.allowed_team_members.map((m: any) => m.guid);
      this.loading_team_members = false;

      // Pre-select the currently-booked date/time so the client sees exactly what's booked as soon as
      // the modal opens, instead of an empty calendar they'd have to hunt through to find it again.
      const start = new Date(this.existing_booking.start_time);
      const end = new Date(this.existing_booking.end_time);
      const duration_minutes = Math.round((end.getTime() - start.getTime()) / 60000);
      this.appointment_info.in_hours = Math.floor(duration_minutes / 60);
      this.appointment_info.in_minutes = duration_minutes % 60;
      this.appointment_info.selected_length = duration_minutes;
      this.original_slot = { start_time: this.existing_booking.start_time, end_time: this.existing_booking.end_time };

      this.calendar_date = start;
      this.appointment_info.date = dayjs(start).format('YYYY-MM-DD');
      this.displayed_year = start.getFullYear();
      this.displayed_month = start.getMonth() + 1;

      if (this.appointment_info.selected_team_members.length > 0) {
        this.LoadAvailableDatesForDisplayedMonth();
        this.GetAvailableSlots();
      }
      return;
    }

    this.GetUser();
    this.GetCompanies();
    this.GetTeamMembers();
  }

  TeamMemberNames(): string {
    return this.allowed_team_members.map((m: any) => m.display_name).join(', ');
  }

  // ── Booking form ──────────────────────────────────────────────────────────

  async GetUser() {
    this.user = await this.globalService.GetUser();
  }

  async GetCompanies() {
    const data: any = await this.apiService.get('companies');
    if (data?.success) {
      this.companies = data.data;
    }
  }

  // Query param carrying who this booking is on behalf of - null when booking as "myself".
  private OnBehalfOfCompanyGuid(): string | null {
    return this.selected_target !== 'myself' ? this.selected_target.guid : null;
  }

  // Availability lookups are scoped to the existing event when managing (team members are fixed there,
  // so permissions are checked against the event itself rather than the client's general access).
  private AvailableSlotsPath(): string {
    return this.is_managing
      ? `appointment/${this.existing_booking.guid}/available_slots`
      : `appointment/available_slots`;
  }

  // Called when "Booking Appointment On Behalf Of" changes - which team members (and their availability)
  // are visible depends entirely on who the booking is for, so everything downstream is reset and re-fetched.
  async OnTargetChange() {
    this.appointment_info.selected_team_members = [];
    this.allowed_team_members = [];
    this.available_dates_set.clear();
    this.calendar_disabled_dates = [];
    this.appointment_info.date = '';
    this.calendar_date = null;
    this.available_slots = [];
    this.appointment_info.selected_slot = null;
    await this.GetTeamMembers();
  }

  async GetTeamMembers() {
    this.loading_team_members = true;
    const company_guid = this.OnBehalfOfCompanyGuid();
    const data: any = await this.apiService.get(
      `appointment/team_members${company_guid ? '?company_guid=' + company_guid : ''}`
    );
    if (data?.success) {
      this.allowed_team_members = data.data;
      if (this.allowed_team_members.length === 1) {
        this.appointment_info.selected_team_members = [this.allowed_team_members[0].guid];
      }
      if (this.appointment_info.selected_team_members.length > 0) {
        await this.LoadAvailableDatesForDisplayedMonth();
      }
    }
    this.loading_team_members = false;
  }

  IsTeamMemberSelected(guid: string): boolean {
    return this.appointment_info.selected_team_members.includes(guid);
  }

  async ToggleTeamMember(guid: string) {
    const idx = this.appointment_info.selected_team_members.indexOf(guid);
    if (idx >= 0) {
      this.appointment_info.selected_team_members.splice(idx, 1);
    } else {
      this.appointment_info.selected_team_members.push(guid);
    }
    await this.OnTeamMembersChange();
  }

  async GetAvailableDates(start_date: string, end_date: string) {
    if (this.appointment_info.selected_team_members.length === 0) return;
    this.loading_dates = true;

    const company_guid = this.OnBehalfOfCompanyGuid();
    const perMemberSets: Set<string>[] = [];
    for (const tm_guid of this.appointment_info.selected_team_members) {
      const data: any = await this.apiService.get(
        `${this.AvailableSlotsPath()}` +
        `?team_member_guid=${tm_guid}` +
        `&start_date=${start_date}&end_date=${end_date}` +
        `&duration=${this.appointment_info.selected_length}` +
        (company_guid ? `&company_guid=${company_guid}` : '')
      );
      const dateSet = new Set<string>();
      if (data?.success && Array.isArray(data.data)) {
        data.data.forEach((slot: { start_time: string }) => {
          dateSet.add(dayjs(slot.start_time).format('YYYY-MM-DD'));
        });
      }
      perMemberSets.push(dateSet);
    }

    let intersection = perMemberSets[0];
    for (let i = 1; i < perMemberSets.length; i++) {
      intersection = new Set(Array.from(intersection).filter(d => perMemberSets[i].has(d)));
    }
    intersection.forEach(d => this.available_dates_set.add(d));
    this.ComputeDisabledDates();
    this.loading_dates = false;
  }

  async LoadAvailableDatesForDisplayedMonth() {
    const year = this.displayed_year;
    const month = this.displayed_month;
    const today = dayjs();
    const monthStart = dayjs(new Date(year, month - 1, 1));
    const start = monthStart.isBefore(today, 'day') ? today.format('YYYY-MM-DD') : monthStart.format('YYYY-MM-DD');
    const lastDay = new Date(year, month, 0).getDate();
    const end = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    if (start > end) return;
    await this.GetAvailableDates(start, end);
  }

  ComputeDisabledDates() {
    const year = this.displayed_year;
    const month = this.displayed_month;
    const daysInMonth = new Date(year, month, 0).getDate();
    const disabled: Date[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      if (!this.available_dates_set.has(dateStr)) {
        disabled.push(new Date(year, month - 1, d));
      }
    }
    this.calendar_disabled_dates = [...disabled];
  }

  async OnCalendarMonthChange(event: { month: number; year: number }) {
    this.displayed_month = event.month;
    this.displayed_year = event.year;
    await this.LoadAvailableDatesForDisplayedMonth();
  }

  OnCalendarDateSelect(date: Date) {
    this.appointment_info.date = dayjs(date).format('YYYY-MM-DD');
    this.appointment_info.selected_slot = null;
    this.GetAvailableSlots();
  }

  async GetAvailableSlots() {
    if (this.appointment_info.selected_team_members.length === 0 || !this.appointment_info.date) {
      this.available_slots = [];
      return;
    }

    this.loading_slots = true;
    this.available_slots = [];
    this.appointment_info.selected_slot = null;

    const company_guid = this.OnBehalfOfCompanyGuid();
    const perMemberSlotMaps: Map<string, any>[] = [];
    for (const tm_guid of this.appointment_info.selected_team_members) {
      const data: any = await this.apiService.get(
        `${this.AvailableSlotsPath()}` +
        `?team_member_guid=${tm_guid}` +
        `&start_date=${this.appointment_info.date}&end_date=${this.appointment_info.date}` +
        `&duration=${this.appointment_info.selected_length}` +
        (company_guid ? `&company_guid=${company_guid}` : '')
      );
      const slotMap = new Map<string, any>();
      if (data?.success && Array.isArray(data.data)) {
        data.data.forEach((slot: any) => slotMap.set(slot.start_time, slot));
      }
      perMemberSlotMaps.push(slotMap);
    }

    let commonKeys = Array.from(perMemberSlotMaps[0].keys());
    for (let i = 1; i < perMemberSlotMaps.length; i++) {
      commonKeys = commonKeys.filter(k => perMemberSlotMaps[i].has(k));
    }
    this.available_slots = commonKeys.sort().map(k => perMemberSlotMaps[0].get(k));

    this.IncludeOriginalSlotIfViewingIt();

    this.loading_slots = false;
  }

  // If we're looking at the date the appointment is currently booked on, and the length hasn't been
  // changed, make sure its own slot is in the list (see original_slot above) and select it - "no change"
  // is always a valid option even though the availability check itself won't offer it.
  private IncludeOriginalSlotIfViewingIt() {
    if (!this.original_slot) return;
    if (this.appointment_info.selected_length !== Math.round(
      (new Date(this.original_slot.end_time).getTime() - new Date(this.original_slot.start_time).getTime()) / 60000
    )) return;
    if (dayjs(this.original_slot.start_time).format('YYYY-MM-DD') !== this.appointment_info.date) return;

    const original_start = new Date(this.original_slot.start_time).getTime();
    const already_listed = this.available_slots.some((s: any) => new Date(s.start_time).getTime() === original_start);
    if (!already_listed) {
      this.available_slots = [...this.available_slots, this.original_slot]
        .sort((a: any, b: any) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
    }
    this.appointment_info.selected_slot = this.available_slots.find(
      (s: any) => new Date(s.start_time).getTime() === original_start
    ) || null;
  }

  async OnTeamMembersChange() {
    this.available_dates_set.clear();
    this.calendar_disabled_dates = [];
    this.appointment_info.date = '';
    this.calendar_date = null;
    this.available_slots = [];
    this.appointment_info.selected_slot = null;
    if (this.appointment_info.selected_team_members.length > 0) {
      await this.LoadAvailableDatesForDisplayedMonth();
    }
  }

  UpdateLength() {
    this.appointment_info.selected_length = (this.appointment_info.in_hours * 60) + this.appointment_info.in_minutes;
    this.available_dates_set.clear();
    this.LoadAvailableDatesForDisplayedMonth();
    this.GetAvailableSlots();
  }

  isAvailableDate(date: { year: number; month: number; day: number }): boolean {
    // PrimeNG date template passes month as 0-indexed
    const dateStr = `${date.year}-${String(date.month + 1).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
    return this.available_dates_set.has(dateStr);
  }

  IsValidEvent(): string {
    let msg = '';
    if (!this.appointment_info.title?.trim()) msg += 'Title is required.<br/>';
    if (!this.appointment_info.date?.trim()) msg += 'Date is required.<br/>';
    if (this.appointment_info.selected_team_members.length === 0) msg += 'At least one team member must be selected.<br/>';
    if (!this.appointment_info.selected_slot) msg += 'A time slot must be selected.<br/>';
    return msg;
  }

  async BookAppointment() {
    const slot = this.appointment_info.selected_slot;
    if (!slot) return;

    const company_guid = this.OnBehalfOfCompanyGuid();
    const body = {
      team_member_guids: this.appointment_info.selected_team_members,
      start_time: new Date(slot.start_time).toISOString(),
      end_time: new Date(slot.end_time).toISOString(),
      title: this.appointment_info.title,
      description: this.appointment_info.description,
      tz_str: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(company_guid ? { company_guid } : {}),
    };

    await this.globalService.presentLoading('Booking appointment...', 99999999999999999);
    const data: any = await this.apiService.post('appointment/book', body);
    await this.globalService.dismissLoading();

    if (data?.success) {
      this.globalService.presentToastMessage('Appointment booked!', true);
      this.modalController.dismiss({ refresh: true });
    } else {
      this.globalService.presentToastMessage(data?.message || 'Failed to book appointment', false);
    }
  }

  async Reschedule() {
    const slot = this.appointment_info.selected_slot;
    if (!slot) return;

    const body = {
      start_time: new Date(slot.start_time).toISOString(),
      end_time: new Date(slot.end_time).toISOString(),
      title: this.appointment_info.title,
      description: this.appointment_info.description,
      tz_str: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    await this.globalService.presentLoading('Rescheduling appointment...', 99999999999999999);
    const data: any = await this.apiService.put(`appointment/${this.existing_booking.guid}`, body);
    await this.globalService.dismissLoading();

    if (data?.success) {
      this.globalService.presentToastMessage('Appointment rescheduled!', true);
      this.modalController.dismiss({ refresh: true });
    } else {
      this.globalService.presentToastMessage(data?.message || 'Failed to reschedule appointment', false);
    }
  }

  async CancelAppointment() {
    const alert = await this.alertController.create({
      header: 'Cancel Appointment',
      message: 'Are you sure you want to cancel this appointment? This cannot be undone.',
      buttons: [
        { text: 'Keep Appointment', role: 'cancel' },
        {
          text: 'Cancel Appointment',
          handler: async () => {
            await this.globalService.presentLoading('Cancelling appointment...', 99999999999999999);
            const data: any = await this.apiService.delete(`appointment/${this.existing_booking.guid}`, {
              tz_str: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            await this.globalService.dismissLoading();

            if (data?.success) {
              this.globalService.presentToastMessage('Appointment cancelled', true);
              this.modalController.dismiss({ refresh: true });
            } else {
              this.globalService.presentToastMessage(data?.message || 'Failed to cancel appointment', false);
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async Close() {
    this.modalController.dismiss({ refresh: false });
  }

}
